import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Edit2, Save, X, AlertTriangle, Plus, Trash2, Palette, Upload, CheckCircle, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";

export function QualificationMatrixTab() {
  const { user } = useAuth();
  const { data: matrix, isLoading, refetch } = trpc.ergos.getQualificationMatrix.useQuery();
  const saveMatrixMutation = trpc.ergos.saveQualificationMatrix.useMutation();
  const publishMutation = trpc.ergos.publishQualificationMatrix.useMutation();
  const cloneMutation = trpc.ergos.cloneQualificationMatrixForEdit.useMutation();
  const utils = trpc.useUtils();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [editedRanges, setEditedRanges] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [colorPickerOpen, setColorPickerOpen] = useState<number | null>(null);
  const [tempColor, setTempColor] = useState<string>('#3B82F6');

  const handleEditClick = async () => {
    if (!matrix) return;
    
    // Se a versão está PUBLISHED, criar clone para edição
    if (matrix.status === "PUBLISHED") {
      try {
        const toastId = toast.loading("Criando nova versão para edição...");
        const result = await cloneMutation.mutateAsync({ versionId: matrix.id });
        toast.dismiss(toastId);
        
        if (result.isClone) {
          toast.success(`Nova versão ${result.newVersion} criada para edição`);
        }
        
        await refetch();
        // Após refetch, iniciar edição com os dados da nova versão
        setIsEditing(true);
        setEditedRanges(JSON.parse(JSON.stringify(matrix.ranges)));
        setErrors([]);
      } catch (error: any) {
        toast.error("Erro ao criar versão para edição", {
          description: error.message || "Ocorreu um erro inesperado.",
        });
      }
    } else {
      setIsEditing(true);
      setEditedRanges(JSON.parse(JSON.stringify(matrix.ranges)));
      setErrors([]);
    }
  };

  const handleSave = async () => {
    try {
      setErrors([]);
      
      // Validacoes
      if (editedRanges.length === 0) {
        setErrors(["Pelo menos uma faixa eh obrigatoria"]);
        return;
      }

      const sorted = [...editedRanges].sort((a, b) => a.pontuacaoMin - b.pontuacaoMin);
      
      if (sorted[0].pontuacaoMin !== 0) {
        setErrors(["A primeira faixa deve comear em 0"]);
        return;
      }
      
      if (sorted[sorted.length - 1].pontuacaoMax !== 100) {
        setErrors(["A ultima faixa deve terminar em 100"]);
        return;
      }

      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].pontuacaoMax + 1 !== sorted[i + 1].pontuacaoMin) {
          setErrors([`Ha lacunas ou sobreposicao entre as faixas ${i + 1} e ${i + 2}`]);
          return;
        }
      }

      for (const range of editedRanges) {
        if (!range.qualificador || !range.qualificador.trim()) {
          setErrors(["Qualificador nao pode ser vazio em todas as faixas"]);
          return;
        }
        if (!range.conclusao || !range.conclusao.trim()) {
          setErrors(["Conclusão não pode ser vazia em todas as faixas"]);
          return;
        }
        if (!range.corHex) {
          setErrors(["Selecione uma cor para todas as faixas"]);
          return;
        }
      }

      // Normalizar payload: converter undefined para null, garantir tipos corretos
      const normalizedRanges = editedRanges.map(range => ({
        pontuacaoMin: Number(range.pontuacaoMin) || 0,
        pontuacaoMax: Number(range.pontuacaoMax) || 0,
        qualificador: (range.qualificador || "").trim(),
        conclusao: (range.conclusao || "").trim(),
        corHex: range.corHex || null  // NUNCA undefined
      }));

      await saveMatrixMutation.mutateAsync({ ranges: normalizedRanges });
      toast.success("Matriz de Qualificacao salva com sucesso!");
      
      // Invalidar queries da Matriz de Qualificação
      await utils.ergos.getQualificationMatrix.invalidate();
      
      // SINCRONIZAÇÃO AUTOMÁTICA: Invalidar queries da Matriz de Classificação (P×S)
      // Isso garante que severidades derivadas sejam atualizadas imediatamente
      try {
        await utils.ergos.classificationMatrix.getCurrentRiskMatrix.invalidate();
      } catch (e) {
        // Ignorar se não existir
      }
      console.log("[QualificationMatrixTab] Queries de Matriz de Classificação invalidadas");
      
      await new Promise(r => setTimeout(r, 500));
      await refetch();
      
      setIsEditing(false);
    } catch (error: any) {
      setErrors([error.message || "Erro ao salvar"]);
      toast.error(error.message || "Erro ao salvar");
    }
  };

  const handlePublish = async () => {
    if (!matrix?.id) return;
    
    setIsPublishing(true);
    try {
      const result = await publishMutation.mutateAsync({ versionId: matrix.id });
      toast.success(result.message || "Matriz publicada com sucesso!", {
        description: `Versão ${result.version} agora está ativa.`,
      });
      await refetch();
    } catch (error: any) {
      const errorMsg = error.message || "Ocorreu um erro ao publicar.";
      if (errorMsg.includes("VALIDATION_ERROR")) {
        toast.error("Validação falhou", {
          description: errorMsg.replace("VALIDATION_ERROR: ", ""),
        });
      } else {
        toast.error("Erro ao publicar", {
          description: errorMsg,
        });
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = async () => {
    setIsEditing(false);
    setEditedRanges([]);
    setErrors([]);
    await refetch();
  };

  const handleAddRange = () => {
    const newRange = {
      pontuacaoMin: 0,
      pontuacaoMax: 100,
      qualificador: "",
      conclusao: "",
      corHex: "#22C55E"  // Default verde, NUNCA undefined
    };
    setEditedRanges([...editedRanges, newRange]);
  };

  const handleDeleteRange = (idx: number) => {
    setEditedRanges(editedRanges.filter((_, i) => i !== idx));
  };

  const displayRanges = isEditing ? editedRanges : (matrix?.ranges || []);
  const isPublished = matrix?.status === "PUBLISHED";
  const isDraft = matrix?.status === "DRAFT";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Matriz de Qualificacao</CardTitle>
            <CardDescription>
              {isEditing ? (
                <span className="text-amber-600 font-medium">Voce esta editando a Matriz de Qualificacao</span>
              ) : (
                <span className="flex items-center gap-2">
                  Versao {matrix?.version || "1.0"} - 
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isPublished 
                      ? "bg-green-100 text-green-800" 
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {isPublished && <CheckCircle size={12} />}
                    {matrix?.status || "DRAFT"}
                  </span>
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {!isEditing ? (
              <>
                <Button onClick={handleEditClick} className="gap-2" disabled={cloneMutation.isPending}>
                  {cloneMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Edit2 size={16} />
                  )}
                  {isPublished ? "Editar (criar nova versão)" : "Editar Matriz"}
                </Button>
                {isDraft && (
                  <Button 
                    onClick={handlePublish} 
                    className="gap-2 bg-green-600 hover:bg-green-700"
                    disabled={isPublishing}
                  >
                    {isPublishing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    Publicar
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button onClick={handleSave} className="gap-2 bg-green-600 hover:bg-green-700">
                  <Save size={16} />
                  Salvar
                </Button>
                <Button onClick={handleCancel} variant="outline" className="gap-2">
                  <X size={16} />
                  Cancelar
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {errors.length > 0 && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="font-semibold mb-2">Erros encontrados:</div>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((err, idx) => (
                  <li key={idx} className="text-sm">{err}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        ) : !isEditing ? (
          // Modo READ-ONLY
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Faixa</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Pontuação</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Conclusão</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRanges.map((range: any, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 text-sm">{idx + 1}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{range.pontuacaoMin} - {range.pontuacaoMax}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: range.corHex || '#3B82F6' }}
                          />
                          <span>{range.qualificador}</span>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{range.conclusao}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Modo EDIÇÃO
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Min</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Max</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Cor</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Qualificador</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Conclusão</th>
                    <th className="border border-gray-300 px-4 py-2 text-center text-sm font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {editedRanges.map((range: any, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-2 py-2">
                        <Input
                          type="number"
                          value={range.pontuacaoMin}
                          onChange={(e) => {
                            const updated = [...editedRanges];
                            updated[idx].pontuacaoMin = parseInt(e.target.value) || 0;
                            setEditedRanges(updated);
                          }}
                          className="w-20"
                          min={0}
                          max={100}
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <Input
                          type="number"
                          value={range.pontuacaoMax}
                          onChange={(e) => {
                            const updated = [...editedRanges];
                            updated[idx].pontuacaoMax = parseInt(e.target.value) || 0;
                            setEditedRanges(updated);
                          }}
                          className="w-20"
                          min={0}
                          max={100}
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <div className="relative">
                          <button
                            onClick={() => {
                              setColorPickerOpen(colorPickerOpen === idx ? null : idx);
                              setTempColor(range.corHex || '#3B82F6');
                            }}
                            className="flex items-center gap-2 px-2 py-1 border rounded hover:bg-gray-50"
                          >
                            <div
                              className="w-6 h-6 rounded border border-gray-300"
                              style={{ backgroundColor: range.corHex || '#3B82F6' }}
                            />
                            <Palette size={16} />
                          </button>
                          {colorPickerOpen === idx && (
                            <div className="absolute z-10 mt-1 p-3 bg-white border rounded shadow-lg">
                              <div className="grid grid-cols-5 gap-2 mb-2">
                                {['#22C55E', '#EAB308', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#10B981', '#6366F1'].map(color => (
                                  <button
                                    key={color}
                                    onClick={() => {
                                      const updated = [...editedRanges];
                                      updated[idx].corHex = color;
                                      setEditedRanges(updated);
                                      setColorPickerOpen(null);
                                    }}
                                    className="w-8 h-8 rounded border-2 border-gray-200 hover:border-gray-400"
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                              </div>
                              <Input
                                type="text"
                                value={tempColor}
                                onChange={(e) => setTempColor(e.target.value)}
                                placeholder="#RRGGBB"
                                className="w-full text-sm"
                              />
                              <Button
                                size="sm"
                                className="w-full mt-2"
                                onClick={() => {
                                  const updated = [...editedRanges];
                                  updated[idx].corHex = tempColor;
                                  setEditedRanges(updated);
                                  setColorPickerOpen(null);
                                }}
                              >
                                Aplicar
                              </Button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <Input
                          value={range.qualificador || ''}
                          onChange={(e) => {
                            const updated = [...editedRanges];
                            updated[idx].qualificador = e.target.value;
                            setEditedRanges(updated);
                          }}
                          placeholder="Ex: Baixo, Médio, Alto"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <Input
                          value={range.conclusao || ''}
                          onChange={(e) => {
                            const updated = [...editedRanges];
                            updated[idx].conclusao = e.target.value;
                            setEditedRanges(updated);
                          }}
                          placeholder="Texto de conclusão"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRange(idx)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={handleAddRange} variant="outline" className="gap-2">
              <Plus size={16} />
              Adicionar Faixa
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
